/*
  Report Modal Component
  ----------------------
  Modal for viewing lead/report details in admin dashboard.
  Reports are generated on-demand from stored result JSON.
*/

import React from 'react';

/**
 * Report preview modal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Object} props.lead - Lead data to display
 * @param {string} props.message - Status message
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onMessage - Message update handler
 * @param {Function} props.onLeadUpdate - Lead update handler (unused but kept for API compatibility)
 */
const ReportModal = ({ 
  isOpen, 
  lead, 
  message, 
  onClose, 
  onMessage,
  // eslint-disable-next-line no-unused-vars
  onLeadUpdate 
}) => {
  if (!isOpen) return null;

  // Helper functions for JSON view
  const money = (x, cur = 'USD') => {
    const n = Number(x);
    return isFinite(n) 
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n) 
      : String(x ?? '-');
  };
  
  const asText = (v) => (v === null || v === undefined || v === '' ? '-' : String(v));

  const renderJsonView = () => {
    const snap = lead?.json || {};
    const fd = snap.formData || {};
    const effScore = snap.efficiencyScore ?? snap.metrics?.efficiencyScore ?? snap.summary?.efficiencyScore ?? null;
    const rpe = snap.revenuePerEmployee ?? snap.metrics?.revenuePerEmployee ?? snap.rpe?.value ?? null;
    const rpeIndustry = snap.industryRPE ?? snap.metrics?.industryRPE ?? snap.rpe?.industryAverage ?? null;
    const aiAvg = snap.aiAgentsAverage || snap.metrics?.aiAgentsAverage || {};
    const savings = snap.savings || {};
    const fte = snap.fteSavings || {};
    const agentImpact = snap.agentImpact || {};

    return (
      <div className="p-4 overflow-auto h-[calc(90vh-60px)]">
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold mb-2">Form Data</h3>
            <div><span className="font-medium">Industry:</span> {asText(fd.industry)}</div>
            <div><span className="font-medium">Employees:</span> {asText(fd.employeeCount)}</div>
            <div><span className="font-medium">Annual Revenue:</span> {money(fd.annualRevenue, fd.currency || 'USD')}</div>
            <div><span className="font-medium">Average FTE Hourly Cost:</span> {money(fd.hourlyCost, fd.currency || 'USD')}</div>
            <div><span className="font-medium">Currency:</span> {asText(fd.currency || 'USD')}</div>
            <div><span className="font-medium">Process Orientation:</span> {asText(fd.processMaturity)}/10</div>
            <div><span className="font-medium">Current Level of Automation:</span> {asText(fd.automationMaturity)}/10</div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">Efficiency & RPE</h3>
            <div><span className="font-medium">Efficiency Score:</span> {asText(effScore)}</div>
            <div><span className="font-medium">Revenue Per Employee (RPE):</span> {money(rpe, fd.currency || 'USD')}</div>
            <div><span className="font-medium">Industry RPE Average:</span> {money(rpeIndustry, fd.currency || 'USD')}</div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">Your Annual Waste Analysis</h3>
            <div>
              <span className="font-medium">AI Agents - Average savings for {asText(fd.industry)}:</span>{' '}
              {asText(Math.round((aiAvg.percentLow || 0) * 100))}% - {asText(Math.round((aiAvg.percentHigh || 0) * 100))}% of revenue
            </div>
            <div>
              <span className="font-medium">AI Agents - Average (amount):</span>{' '}
              {money(aiAvg.amountLow, fd.currency || 'USD')} - {money(aiAvg.amountHigh, fd.currency || 'USD')}
            </div>
            <div>
              <span className="font-medium">Estimated Annual Savings:</span>{' '}
              {money(savings.estimatedAnnualSavings ?? savings.estimatedAnnualSavingsFormatted, fd.currency || 'USD')}
            </div>
            <div>
              <span className="font-medium">Annual hours of human equivalent work:</span>{' '}
              {asText((fte.hours ?? fte.totalHours ?? fte.total)?.toLocaleString?.('en-US') || fte.hours || fte.totalHours || fte.total || '-')}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">Impact from Selected Agents</h3>
            <div>
              <span className="font-medium">Direct Savings:</span>{' '}
              {money(agentImpact.laborSavings ?? agentImpact.labor, fd.currency || 'USD')}
            </div>
            <div>
              <span className="font-medium">Sales/Capacity Opportunity:</span>{' '}
              {money(agentImpact.revenueImpact ?? agentImpact.revenue, fd.currency || 'USD')}
            </div>
            <div>
              <span className="font-medium">Task Improvement:</span>{' '}
              {asText(typeof agentImpact.taskImprovement === 'number' 
                ? `${Math.round(agentImpact.taskImprovement * 100)}%` 
                : agentImpact.taskImprovement)}
            </div>
            <div>
              <span className="font-medium">Total Agent-Driven Annual Benefit:</span>{' '}
              {money(agentImpact.total, fd.currency || 'USD')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReportView = () => {
    // Reports are generated on-demand from the stored result JSON
    if (lead?.runId) {
      return (
        <iframe 
          key={lead.runId} 
          title="Report" 
          src={`/.netlify/functions/get-report?runId=${encodeURIComponent(lead.runId)}`} 
          className="w-full h-[calc(90vh-60px)]"
        />
      );
    }
    
    return (
      <div className="p-4 text-sm text-gray-700">No report available for this entry.</div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" 
      onClick={onClose}
    >
      <div 
        className="bg-white rounded shadow-xl w-[95vw] h-[90vh] max-w-[1400px] relative" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">
              {lead?.view === 'json' ? (
                <>
                  {lead?.headerWhen || '-'} • {lead?.headerIp || '-'} • {lead?.headerOrg || '-'}
                </>
              ) : (
                <>
                  {lead?.name || '-'} • {lead?.email || '-'} • {lead?.company || '-'}
                </>
              )}
            </div>
          </div>
          <button 
            className="text-sm px-3 py-1 bg-gray-200 rounded" 
            onClick={onClose}
          >
            Close
          </button>
        </div>
        
        {/* Content */}
        <div className="w-full h-full">
          {lead?.view === 'json' ? renderJsonView() : renderReportView()}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
